import * as React from 'react'
import styled from '../styles/styled-components'
import { Card, ICard } from './Card'
import { Spinner } from './Spinner'

const Align = styled.div`
  text-align: right;
  margin-bottom: 22px;

  ${p => p.theme.media.sm`
    text-align: left;
  `};

  ${p => p.theme.media.mdOnly`
    margin-bottom: 2vw;
  `};
`
const Till = styled.div`
  font-size: 28px;
  font-weight: 600;

  ${p => p.theme.media.mdOnly`
    font-size: 2.73vw;
  `};
`
const Sub = styled.div`
  color: #8e8e93;
  font-size: 16px;
  height: 20px;

  ${p => p.theme.media.mdOnly`
    font-size: 1.56vw;
    height: 2vw;
  `};
`

const TillHeader: React.SFC<{ till: MaybeEnabled }> = ({ till }) => {
  if (!till) {
    return <Align />
  }

  return (
    <Align>
      <Till>{till ? till : 'Contest finished'}</Till>
      <Sub>{till ? 'Contest ends' : ''}</Sub>
    </Align>
  )
}

const StyledMain = styled.main`
  ${p => p.theme.media.sm`
    max-width: 400px;
    margin: 0 auto;
  `};

  header {
    font-size: 34px;
    margin-bottom: 22px;
    font-weight: 600;

    ${p => p.theme.media.sm`
      margin-bottom: 0;
    `};
  }

  h1 {
    font-size: 38px;
    margin: 10px 0 20px;
  }

  h2 {
    font-size: 28px;
  }

  ${p => p.theme.media.mdOnly`
    header {
      font-size: 3.35vw;
      margin-bottom: 2vw;
    }

    h2 {
      font-size: 2.75vw;
    }
  `};

  h3 {
    font-size: 28px;
    font-weight: 600;
    margin: 30px 0;

    ${p => p.theme.media.mdOnly`
      font-size: 2.75vw;
    `};
  }
`
const MediumBlock = styled.section`
  margin-bottom: 38px;

  p {
    font-size: 1.2em;
  }

  a {
    text-decoration: underline;

    h5 {
      color: ${p => p.theme.colors.brand};
    }
  }
`
const Leaders = styled.section`
  display: flex;

  ${p => p.theme.media.sm`
    header {
      margin-bottom: 0;
    }
  `};

  ${p => p.theme.media.mdOnly`
    height: 34.18vw;
  `};
`
const LeadersLg = styled(Leaders)`
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: flex-start;
  height: 350px;
  margin-bottom: 30px;

  & > div {
    flex-basis: 31.5%;
    width: 31.5%;
    max-width: 31.5%;
    height: 100%;
  }

  ${p => p.theme.media.sm`
    display: none;
  `};
`
const LeadersSm = styled(Leaders)`
  display: none;

  ${p => p.theme.media.sm`
    display: block;
  `};
`
const Column = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
`
const BoardContainer = styled.section`
  h3 {
    ${p => p.theme.media.sm`
      display: none
    `};
  }
`

const Spacer = styled.div`
  height: 25px;
  ${p => p.theme.media.md`
    height: 15px;
  `};
`

const Board = styled.div`
  display: flex;
  flex-flow: row wrap;

  ${p => p.theme.media.sm`
    flex-flow: column nowrap;
  `};

  & > div {
    margin-bottom: 20px;

    ${p => p.theme.media.sm`
      width: 100%;
      max-width: 100%;
    `};

    ${p => p.theme.media.mdOnly`
      flex-basis: 31.5%;
      width: 31.5%;
      max-width: 31.5%;
      margin-right: 2.75%

      &:nth-child(3n) {
        margin-right: 0;
      }
    `};

    ${p => p.theme.media.lg`
      flex-basis: 23%;
      width: 23%;
      max-width: 23%;
      margin-right: 2.65%;

      &:nth-child(4n) {
        margin-right: 0;
      }
    `};
  }
`
const LoadMore = styled.button<{ isFetching: boolean }>`
  cursor: pointer;
  display: block;
  margin: 22px auto;
  font-size: 22px;
  padding: 12px 22px;
  border: 0;
  outline: 0;
  border-radius: 6px;
  background-color: rgba(0, 0, 0, 0.03);
  color: ${p => (p.isFetching ? p.theme.colors.gray.calm : p.theme.colors.brand)};

  &:hover {
    background-color: rgba(0, 0, 0, 0.07);
  }

  ${p => p.theme.media.sm`
    width: 100%;
  `};
`
const StyledSpinner = styled(Spinner)`
  display: inline-block;
  margin: 0 0 0 1em;
  width: auto;
`

export type MaybeEnabled = string | boolean
export interface IEnhancedCardListProps {
  leaders: ICard[]
  others: ICard[]
  error: string | null
  till: MaybeEnabled
  isFetching: boolean
  showMore: boolean
  fetchCandidates(): void
}

export const CardList: React.SFC<IEnhancedCardListProps> = ({
  till,
  others,
  leaders,
  showMore,
  isFetching,
  fetchCandidates,
}) => (
  <StyledMain>
    <MediumBlock>
      <h1>“CAPP for Spin” contest</h1>
      <p>
        CAPP for Spin — a contest for our active community members{' '}
        <strong>to raise the level of awareness of the Cappasity platform and the CAPP</strong>{' '}
        token. Prizes — up to <strong>120,000 CAPP</strong>. We will choose 5 winners who receive
        the biggest number of votes, and first 1000 people who vote for the winners will receive 100
        CAPP to their wallet.{' '}
        <a
          href="https://medium.com/cappasity-blog/capp-for-spin-contest-5c0a79821b9f"
          rel="noopener noreferrer"
          target="Medium"
        >
          Read detailed rules...
        </a>
      </p>
    </MediumBlock>
    <LeadersSm>
      <header>{'Contest Participants'}</header>
      <TillHeader till={till} />
      <Card card={leaders[0]} place={1} leader />
      <Card card={leaders[1]} place={2} leader />
      <Card card={leaders[2]} place={3} leader />
    </LeadersSm>
    <LeadersLg>
      <Column>
        <header>{'Contest leaders'}</header>
        <Card card={leaders[1]} place={2} leader />
      </Column>
      <Column>
        <Spacer />
        <Card card={leaders[0]} place={1} leader />
      </Column>
      <Column>
        <TillHeader till={till} />
        <Card card={leaders[2]} place={3} leader />
      </Column>
    </LeadersLg>
    {!!others.length && (
      <BoardContainer>
        <h3>{'All Contestants'}</h3>
        <Board>
          {others.map((card, idx) => (
            <Card card={card} place={idx + 4} key={`${card.href}-${idx}`} />
          ))}
        </Board>
      </BoardContainer>
    )}
    {showMore && (
      <LoadMore isFetching={isFetching} onClick={fetchCandidates}>
        {'Load more models'}
        {isFetching && <StyledSpinner size={22} />}
      </LoadMore>
    )}
  </StyledMain>
)



// WEBPACK FOOTER //
// ./src/components/CardList.tsx